import React, { Children } from 'react';
import { Shield, ArrowLeft, AlertCircle } from 'lucide-react';
interface PrivacyPolicyProps {
  onNavigate: (page: string) => void;
}
export function PrivacyPolicy({
  onNavigate
}: PrivacyPolicyProps) {
  return <div className="min-h-screen bg-white" data-id="element-809">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-10" data-id="element-810">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-811">
          <button onClick={() => onNavigate('landing')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-812">
            <ArrowLeft className="w-4 h-4" data-id="element-813" />
            <span className="text-sm font-medium" data-id="element-814">Back to Home</span>
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-815">
        <div className="mb-8" data-id="element-816">
          <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-817">
            Privacy Policy
          </h1>
          <p className="text-gray-600" data-id="element-818">Last Updated: January 27, 2026</p>
        </div>

        {/* Critical Warning */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8" data-id="element-819">
          <div className="flex items-start gap-3" data-id="element-820">
            <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-821" />
            <div data-id="element-822">
              <h2 className="text-lg font-bold text-blue-900 mb-2" data-id="element-823">
                Beta Version Privacy Notice
              </h2>
              <p className="text-blue-800 text-sm leading-relaxed" data-id="element-824">
                This beta version stores all data locally in your browser. We do
                NOT have access to your case documentation, incidents, evidence,
                or personal information entered into the application. The only
                data we collect is email addresses for waitlist notifications.
              </p>
            </div>
          </div>
        </div>

        <div className="prose prose-gray max-w-none" data-id="element-825">
          {/* Section 1 */}
          <section className="mb-10" data-id="element-826">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-827">
              1. Overview
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-828">
              Zapiis ("we," "our," or "us") is committed to protecting your
              privacy. This Privacy Policy explains how we collect, use,
              disclose, and safeguard your information when you use our Service.
            </p>
            <p className="text-gray-700 leading-relaxed" data-id="element-829">
              Because this is a beta version with local-only storage, our data
              collection is minimal. This policy will be updated significantly
              when we launch the full version with cloud storage.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-10" data-id="element-830">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-831">
              2. Information We Collect
            </h2>

            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-832">
              2.1 Email Addresses (Waitlist Only)
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-833">
              The ONLY personal information we collect directly is:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4" data-id="element-834">
              <li data-id="element-835">
                <strong data-id="element-836">Email addresses submitted to our waitlist</strong> -
                Used solely to notify you when the full version of Zapiis
                launches
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-837">
              <strong data-id="element-838">Purpose:</strong> We collect waitlist email addresses only
              to contact you about:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4" data-id="element-839">
              <li data-id="element-840">
                Launch announcements when Zapiis becomes publicly available
              </li>
              <li data-id="element-841">Early access opportunities for waitlist members</li>
              <li data-id="element-842">Major updates about HIPAA compliance and new features</li>
            </ul>
            <p className="text-gray-700 leading-relaxed" data-id="element-843">
              We will NOT sell, rent, or share your email address with third
              parties for marketing purposes. You can unsubscribe from waitlist
              communications at any time.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6" data-id="element-844">
              2.2 Data You Enter Into the Application
            </h3>
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4" data-id="element-845">
              <p className="text-green-900 font-bold mb-2" data-id="element-846">
                IMPORTANT: We Do NOT Have Access to Your Data
              </p>
              <p className="text-green-800 text-sm leading-relaxed" data-id="element-847">
                All case documentation, incidents, evidence metadata, people
                information, check-ins, and personal notes you enter into the
                beta application are stored ONLY in your browser's local
                storage. This data never leaves your device and we cannot access
                it.
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-848">
              Data stored locally in your browser includes:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-849">
              <li data-id="element-850">
                Incident documentation (titles, descriptions, dates, locations)
              </li>
              <li data-id="element-851">
                Evidence metadata (file names, descriptions - actual files are
                not uploaded)
              </li>
              <li data-id="element-852">
                People information (names, roles, contact information, notes)
              </li>
              <li data-id="element-853">
                Health check-ins (mood tracking, symptoms, emotional impact)
              </li>
              <li data-id="element-854">
                Profile information (name, email, phone, case description)
              </li>
              <li data-id="element-855">Lawyer preparation notes and intake question responses</li>
            </ul>

            <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6" data-id="element-856">
              2.3 Technical Information
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-857">
              We may automatically collect limited technical information:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-858">
              <li data-id="element-859">Browser type and version</li>
              <li data-id="element-860">Device type (desktop, mobile, tablet)</li>
              <li data-id="element-861">Operating system</li>
              <li data-id="element-862">IP address (for security and analytics)</li>
              <li data-id="element-863">Pages visited and time spent on the Service</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="mb-10" data-id="element-864">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-865">
              3. How We Use Your Information
            </h2>

            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-866">
              3.1 Waitlist Email Addresses
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-867">
              We use email addresses collected from the waitlist to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-868">
              <li data-id="element-869">
                Send launch announcements when Zapiis becomes publicly available
              </li>
              <li data-id="element-870">
                Provide early access opportunities to beta testers and waitlist
                members
              </li>
              <li data-id="element-871">
                Communicate important updates about HIPAA compliance and
                security features
              </li>
              <li data-id="element-872">Respond to inquiries about the Service</li>
            </ul>

            <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6" data-id="element-873">
              3.2 Technical Information
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-874">
              We use technical information to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-875">
              <li data-id="element-876">Improve the Service and user experience</li>
              <li data-id="element-877">Monitor and analyze usage patterns</li>
              <li data-id="element-878">Detect and prevent security threats</li>
              <li data-id="element-879">Troubleshoot technical issues</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="mb-10" data-id="element-880">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-881">
              4. Data Storage and Security
            </h2>

            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-882">
              4.1 Local Storage (Beta Version)
            </h3>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-4" data-id="element-883">
              <div className="flex items-start gap-3" data-id="element-884">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-885" />
                <div data-id="element-886">
                  <p className="text-yellow-900 font-bold mb-2" data-id="element-887">
                    NOT HIPAA Compliant - NOT Encrypted
                  </p>
                  <p className="text-yellow-800 text-sm leading-relaxed" data-id="element-888">
                    The beta version stores all your data in your browser's
                    localStorage. This storage is:
                  </p>
                  <ul className="mt-2 space-y-1 text-yellow-800 text-sm" data-id="element-889">
                    <li data-id="element-890">• NOT encrypted at rest</li>
                    <li data-id="element-891">• NOT backed up to cloud servers</li>
                    <li data-id="element-892">
                      • NOT protected by HIPAA-compliant security measures
                    </li>
                    <li data-id="element-893">
                      • Vulnerable to loss if you clear your browser cache
                    </li>
                    <li data-id="element-894">
                      • Only accessible on the specific browser/device where you
                      entered it
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6" data-id="element-895">
              4.2 Your Responsibilities
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-896">
              Because data is stored locally, you are responsible for:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-897">
              <li data-id="element-898">
                Regularly exporting backups of your data using the export
                functionality
              </li>
              <li data-id="element-899">
                Protecting your device and browser from unauthorized access
              </li>
              <li data-id="element-900">
                Understanding that clearing browser data will permanently delete
                your information
              </li>
              <li data-id="element-901">
                Not entering highly sensitive information that requires
                HIPAA-level protection
              </li>
            </ul>

            <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6" data-id="element-902">
              4.3 Future Cloud Storage
            </h3>
            <p className="text-gray-700 leading-relaxed" data-id="element-903">
              When we launch the full version with cloud storage, data will be
              encrypted in transit and at rest, backed up regularly, and
              protected according to HIPAA standards. A new Privacy Policy will
              be provided at that time.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-10" data-id="element-904">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-905">
              5. Data Sharing and Disclosure
            </h2>

            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-906">
              5.1 We Do NOT Sell Your Data
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-907">
              We do not sell, rent, or trade your personal information to third
              parties for marketing purposes.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-908">
              5.2 Limited Sharing
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-909">
              We may share information only in these circumstances:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-910">
              <li data-id="element-911">
                <strong data-id="element-912">Service Providers:</strong> With trusted third-party
                services that help us operate the Service (e.g., email delivery
                for waitlist notifications)
              </li>
              <li data-id="element-913">
                <strong data-id="element-914">Legal Requirements:</strong> If required by law, court
                order, or government regulation
              </li>
              <li data-id="element-915">
                <strong data-id="element-916">Safety and Security:</strong> To protect the rights,
                property, or safety of Zapiis, our users, or the public
              </li>
              <li data-id="element-917">
                <strong data-id="element-918">Business Transfers:</strong> In connection with a
                merger, acquisition, or sale of assets (with notice to affected
                users)
              </li>
            </ul>

            <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6" data-id="element-919">
              5.3 Your Exported Data
            </h3>
            <p className="text-gray-700 leading-relaxed" data-id="element-920">
              When you export your documentation (JSON or text files), you
              control who receives that data. We are not responsible for how you
              share or use exported files.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-10" data-id="element-921">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-922">
              6. Your Privacy Rights
            </h2>

            <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-923">
              6.1 Waitlist Email Addresses
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-924">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-925">
              <li data-id="element-926">
                Unsubscribe from waitlist emails at any time (link provided in
                all emails)
              </li>
              <li data-id="element-927">Request deletion of your email address from our waitlist</li>
              <li data-id="element-928">
                Request confirmation of whether we have your email address on
                file
              </li>
            </ul>

            <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6" data-id="element-929">
              6.2 Locally Stored Data
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-930">
              Because your case data is stored only in your browser, you have
              complete control:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-931">
              <li data-id="element-932">
                You can delete all data by clearing your browser's localStorage
              </li>
              <li data-id="element-933">
                You can export your data at any time using the export
                functionality
              </li>
              <li data-id="element-934">
                We cannot access, modify, or delete your locally stored data
              </li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="mb-10" data-id="element-935">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-936">
              7. Cookies and Tracking
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-937">
              The beta version uses minimal cookies and tracking:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-938">
              <li data-id="element-939">
                <strong data-id="element-940">Session Storage:</strong> Used to maintain your beta
                login session (clears when browser closes)
              </li>
              <li data-id="element-941">
                <strong data-id="element-942">Local Storage:</strong> Used to store your case
                documentation locally in your browser
              </li>
              <li data-id="element-943">
                <strong data-id="element-944">Analytics:</strong> We may use basic analytics to
                understand how the Service is used (anonymized data)
              </li>
            </ul>
          </section>

          {/* Section 8 */}
          <section className="mb-10" data-id="element-945">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-946">
              8. Third-Party Services
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-947">
              The Service may integrate with third-party services:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-948">
              <li data-id="element-949">
                <strong data-id="element-950">AI Services:</strong> Case summaries and attorney search
                features may use third-party AI APIs (data sent is anonymized
                where possible)
              </li>
              <li data-id="element-951">
                <strong data-id="element-952">Email Services:</strong> Waitlist emails are sent
                through third-party email delivery services
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4" data-id="element-953">
              These third parties have their own privacy policies. We are not
              responsible for their practices.
            </p>
          </section>

          {/* Section 9 */}
          <section className="mb-10" data-id="element-954">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-955">
              9. Children's Privacy
            </h2>
            <p className="text-gray-700 leading-relaxed" data-id="element-956">
              The Service is not intended for use by individuals under the age
              of 18. We do not knowingly collect personal information from
              children. If you believe we have collected information from a
              child, please contact us immediately.
            </p>
          </section>

          {/* Section 10 */}
          <section className="mb-10" data-id="element-957">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-958">
              10. Changes to This Privacy Policy
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-959">
              We may update this Privacy Policy from time to time. Significant
              changes will be communicated through:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2" data-id="element-960">
              <li data-id="element-961">Notice on the Service</li>
              <li data-id="element-962">Email to waitlist members (for major changes)</li>
              <li data-id="element-963">Updated "Last Updated" date at the top of this policy</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4" data-id="element-964">
              When we launch the full version with cloud storage and HIPAA
              compliance, we will provide a completely new Privacy Policy and
              require your acceptance before migrating any data.
            </p>
          </section>

          {/* Section 11 */}
          <section className="mb-10" data-id="element-965">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-966">
              11. Contact Us
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-967">
              For questions about this Privacy Policy or to exercise your
              privacy rights, contact us at:
            </p>
            <div className="bg-gray-50 rounded-lg p-4" data-id="element-968">
              <p className="text-gray-900 font-medium" data-id="element-969">
                Email: [Your Privacy Contact Email]
              </p>
              <p className="text-gray-600 text-sm mt-2" data-id="element-970">
                We will respond to privacy inquiries within 30 days.
              </p>
            </div>
          </section>

          {/* Final Summary */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mt-12" data-id="element-971">
            <p className="text-blue-900 font-bold mb-3" data-id="element-972">
              Privacy Summary for Beta Version:
            </p>
            <ul className="space-y-2 text-blue-800 text-sm" data-id="element-973">
              <li data-id="element-974">
                ✓ We only collect email addresses for waitlist notifications
              </li>
              <li data-id="element-975">
                ✓ Your case data is stored locally in your browser - we cannot
                access it
              </li>
              <li data-id="element-976">
                ✓ Data is NOT encrypted or HIPAA compliant in this beta version
              </li>
              <li data-id="element-977">✓ We do NOT sell or share your information for marketing</li>
              <li data-id="element-978">✓ You can unsubscribe from emails anytime</li>
              <li data-id="element-979">✓ Export your data regularly to prevent loss</li>
            </ul>
          </div>
        </div>
      </main>
    </div>;
}